import React from 'react';
import { parseMarkdown } from '../../utils/sharedResponseUtils';
import CollapsibleList from '../CollapsibleList/CollapsibleList';

/**
 * SmartMarkdown Component
 * 
 * A smart markdown renderer that:
 * - Auto-linkifies URLs and emails
 * - Linkifies attachment filenames to download links
 * - Renders lists with CollapsibleList for better UX
 * - Provides consistent, beautiful formatting
 * - Handles code blocks, links, bold, italic, etc.
 * 
 * @param {Object} props
 * @param {string} props.content - The markdown content to render
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.enableCollapsibleLists - Enable collapsible lists (default: true)
 * @param {number} props.minItemsForCollapse - Minimum items to enable collapse (default: 2)
 * @param {Array} props.attachments - Array of attachment metadata for linkification
 */
const SmartMarkdown = ({ 
  content, 
  className = '', 
  enableCollapsibleLists = true,
  minItemsForCollapse = 2,
  attachments = []
}) => {
  if (!content || typeof content !== 'string') {
    return null;
  }

  /**
   * Linkify attachment filenames to download links
   * Converts filenames mentioned in text to clickable markdown links
   */
  const linkifyAttachments = (text) => {
    if (!attachments || attachments.length === 0) {
      return text;
    }

    let result = text;
    
    // Sort attachments by filename length (longest first) to avoid partial matches
    const sortedAttachments = [...attachments].sort((a, b) => 
      b.filename.length - a.filename.length
    );

    sortedAttachments.forEach(attachment => {
      // Escape special regex characters in filename
      const escapedFilename = attachment.filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      // Match filename (case-insensitive, not inside existing links)
      const filenameRegex = new RegExp(
        `(?<!\\[)\\b(${escapedFilename})\\b(?![\\]\\)])`,
        'gi'
      );
      
      // Replace with markdown link
      result = result.replace(
        filenameRegex,
        `[📎 $1](${attachment.viewUrl})`
      );
    });

    return result;
  };

  /**
   * Format message content: Convert plain URLs and emails to markdown links
   */
  const formatMessageContent = (text) => {
    if (!text) return '';

    // Helper: Extract and preserve existing markdown links
    const preserveMarkdownLinks = (str) => {
      const linkPlaceholders = [];
      let placeholderIndex = 0;
      
      const linkPattern = /\[([^\]]+)\]\(([^\)]+)\)/g;
      const preservedText = str.replace(linkPattern, (fullMatch, linkText, linkUrl) => {
        const placeholder = `__MARKDOWN_LINK_${placeholderIndex}__`;
        linkPlaceholders.push({ placeholder, fullMatch });
        placeholderIndex++;
        return placeholder;
      });
      
      return { preservedText, linkPlaceholders };
    };

    // Helper: Trim trailing punctuation from URLs/emails
    const trimTrailingPunctuation = (str) => {
      if (!str) return { core: '', trailing: '' };
      
      // Remove trailing punctuation: ], ), }, ., ;, :, !
      const trailingPunctuationRegex = /([\]\)\}\.,;:!])+$/;
      const match = str.match(trailingPunctuationRegex);
      
      if (!match) {
        return { core: str, trailing: '' };
      }
      
      const punctIndex = str.length - match[0].length;
      const core = str.substring(0, punctIndex);
      const trailing = str.substring(punctIndex);
      
      // Always remove closing brackets/braces - they break markdown
      if (trailing.match(/^[\]\)\}]+/)) {
        return { core, trailing };
      }
      
      // Validate other punctuation
      try {
        new URL(str); // Full string is valid URL, keep punctuation
        return { core: str, trailing: '' };
      } catch {
        return { core, trailing }; // Not valid with punctuation, remove it
      }
    };

    // Helper: Add word-break opportunities for long strings
    const addBreakOpportunities = (str) => {
      if (str && str.length > 40) {
        return str
          .replace(/\//g, '/\u200B')
          .replace(/\?/g, '?\u200B')
          .replace(/&/g, '&\u200B')
          .replace(/=/g, '=\u200B')
          .replace(/-/g, '-\u200B')
          .replace(/_/g, '_\u200B');
      }
      return str;
    };

    // Helper: Check if position is inside a markdown link
    const isInsideMarkdownLink = (str, pos) => {
      const before = str.substring(0, pos);
      const after = str.substring(pos);
      
      const lastBracketOpen = before.lastIndexOf('[');
      const lastBracketClose = before.lastIndexOf(']');
      
      if (lastBracketOpen > lastBracketClose) {
        const parenOpen = after.indexOf('(');
        const parenClose = after.indexOf(')');
        if (parenOpen !== -1 && parenOpen < parenClose) {
          return true;
        }
      }
      
      return false;
    };

    // Preserve existing markdown links
    const { preservedText, linkPlaceholders } = preserveMarkdownLinks(text);
    let result = preservedText;

    // Linkify emails
    result = result.replace(
      /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})(?![^\s]|\])/g, 
      (match, email, offset) => {
        if (isInsideMarkdownLink(result, offset)) {
          return match;
        }
        const { core, trailing } = trimTrailingPunctuation(email);
        return `[${core}](mailto:${core})${trailing}`;
      }
    );

    // Linkify URLs with http/https
    result = result.replace(
      /((?:https?:\/\/)[^\s<\[\]()}\]]+?)(?=[\]\)\}\.,;:!]|\s|$)/g, 
      (match, url, offset) => {
        if (match.includes('__MARKDOWN_LINK_') || isInsideMarkdownLink(result, offset)) {
          return match;
        }
        if (url.includes(' ')) {
          return match;
        }
        
        const { core, trailing } = trimTrailingPunctuation(url);
        try {
          new URL(core);
          const pretty = addBreakOpportunities(core.replace(/^https?:\/\//, ''));
          return `[${pretty}](${core})${trailing}`;
        } catch {
          return match;
        }
      }
    );

    // Linkify URLs starting with www.
    result = result.replace(
      /(www\.[^\s<\[\]()}\]]+?)(?=[\]\)\}\.,;:!]|\s|$)/g, 
      (match, url, offset) => {
        if (match.includes('__MARKDOWN_LINK_') || isInsideMarkdownLink(result, offset)) {
          return match;
        }
        if (url.includes(' ')) {
          return match;
        }
        
        const { core, trailing } = trimTrailingPunctuation(url);
        try {
          new URL(`https://${core}`);
          const pretty = addBreakOpportunities(core);
          return `[${pretty}](https://${core})${trailing}`;
        } catch {
          return match;
        }
      }
    );

    // Restore preserved markdown links
    if (linkPlaceholders && linkPlaceholders.length > 0) {
      linkPlaceholders.forEach(({ placeholder, fullMatch }) => {
        result = result.replace(placeholder, fullMatch);
      });
    }

    return result;
  };

  // Step 1: Linkify attachment filenames first
  const attachmentLinkedContent = linkifyAttachments(content);
  
  // Step 2: Format the content (linkify URLs, emails, etc.)
  const formattedContent = formatMessageContent(attachmentLinkedContent);

  // Check if content has list pattern for CollapsibleList
  const hasListPattern = enableCollapsibleLists && (
    /^\s*[\d]+\.\s+.+/m.test(formattedContent) || // Numbered list
    /^\s*[•\-\*]\s+.+/m.test(formattedContent) // Bulleted list
  );

  const baseClasses = 'smart-markdown';
  const combinedClasses = `${baseClasses} ${className}`.trim();

  if (hasListPattern) {
    return (
      <div className={`${combinedClasses} whitespace-pre-wrap`}>
        <CollapsibleList 
          content={formattedContent} 
          minItems={minItemsForCollapse} 
        />
      </div>
    );
  }

  // Regular markdown rendering
  return (
    <div 
      className={`${combinedClasses} whitespace-pre-wrap break-words`}
      dangerouslySetInnerHTML={{ 
        __html: parseMarkdown(formattedContent) 
      }}
    />
  );
};

export default SmartMarkdown;
